import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaFileInvoice, FaTrashAlt, FaSearch } from "react-icons/fa";
import useAuth from "../../../../Hooks/useAuth";
import usePermission from "../../../../Hooks/usePermission";
import { useDebounce } from "../../../../Hooks/useDebounce";
import { useDeleteBillMutation } from "../../../../store";
import { useLazyGetBillsQuery } from "../../../../store/storageRoles/billsApi";
import BillDetailDrawer from "../BillDetailDrawer";
import ConfirmationModal from "../ConfirmationModal";
import CustomPagination from "../CustomPagination";
import FormatDateHourString from "../../../Utils/FormatDateHourString";
import { getBillTypeDetails } from "../../../../utils/billTypeDetails";

const PAGE_SIZE = 100;

/** Fatura açıklaması — "Bedelsiz Yükleme -" / "İade Alma -" önekleri soyulur. */
export const extractBillNote = (bill) => {
  if (!bill?.shippingInfo) return "";
  const info = bill.shippingInfo.trim();
  // Kota paylaşımı soyulmaz: açıklama cümlesi bilginin kendisi (2026-08-28)
  for (const p of ["Bedelsiz Yükleme", "İade Alma"]) {
    if (info.startsWith(p)) return info.slice(p.length).replace(/^[\s-]+/, "").trim();
  }
  if (bill.billType === "Bill") return "";
  return info;
};

/**
 * Fatura kalemlerini satır gruplarına toplar (Faturalar sekmesindeki kural):
 * anahtar = fatura no + tür + paylaşım yönü + TAM zaman damgası; grup içinde
 * miktar ürün tipine göre toplanır, tutar = income toplamı (birim fiyat
 * toplanmaz). Yeniden eskiye sıralı.
 */
export const groupBills = (bills = []) => {
  const map = new Map();
  [...bills]
    .sort((a, b) => new Date(b.createdInfo) - new Date(a.createdInfo))
    .forEach((bill) => {
      const timeKey = bill.createdInfo ? new Date(bill.createdInfo).getTime() : "-";
      const directionKey = bill.quotaShare?.direction || "-";
      const key = `${bill.billNo || "-"}__${bill.billType || "-"}__${directionKey}__${timeKey}`;
      let g = map.get(key);
      if (!g) {
        g = {
          key,
          billNo: bill.billNo,
          billType: bill.billType,
          quotaShare: bill.quotaShare,
          billerName: bill.billerName,
          firmApellation: bill.firmApellation,
          firmName: bill.firmName,
          isSubFirmBill: !!bill.isSubFirmBill,
          createdInfo: bill.createdInfo,
          totalIncome: 0,
          products: new Map(),
          lines: [],
        };
        map.set(key, g);
      }
      if (new Date(bill.createdInfo) > new Date(g.createdInfo)) g.createdInfo = bill.createdInfo;
      g.totalIncome += Number(bill.income) || 0;
      const typeName = bill.productTypeName || bill.productName || "-";
      g.products.set(typeName, (g.products.get(typeName) || 0) + (Number(bill.quota) || 0));
      g.lines.push(bill);
    });
  return Array.from(map.values()).map((g) => ({
    ...g,
    productSummary: Array.from(g.products.entries()).map(([name, quota]) => ({ name, quota })),
  }));
};

const money = (n) => `₺${Number(n).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`;

/**
 * Firma faturaları — Cihazlar sayfasının sağ sütunu (#316). Eski Faturalar
 * sekmesinin listesi: kapsam (firma + alt firmalar) sayfanın firma
 * seçicisinden gelir; kota kartları ve Bedelsiz / İade düğmeleri sayfanın
 * üstünde ortaktır, burada tekrar edilmez.
 *
 * @param {string[]} firmIds       kapsam; boş = firma + tüm alt firmalar
 * @param {boolean} showFirmName   birden fazla firma seçiliyken kartta firma adı
 * @param {number} refreshKey      değişince liste yeniden çekilir (kota işlemleri)
 * @param {Function} onFirmsLoaded sunucunun kapsam firmaları (seçici seçenekleri)
 * @param {Function} onBillDeleted silme sonrası (kotalar değişti → sayfayı yenile)
 * @param {Function} onNotify      ({severity, message})
 */
export default function FirmBillsPanel({
  firm,
  firmIds = [],
  showFirmName = false,
  readOnly = false,
  refreshKey = 0,
  onFirmsLoaded,
  onBillDeleted,
  onNotify,
}) {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const id = firm?._id;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const debouncedPage = useDebounce(page, 200);
  const [detail, setDetail] = useState({ open: false, bill: null });
  const [confirm, setConfirm] = useState({ open: false, bill: null });
  const [skipQuotaEffect, setSkipQuotaEffect] = useState(false);

  const [getBills, { data: billsResponse, isLoading, isFetching, isError }] = useLazyGetBillsQuery();
  const [deleteBill, deleteResult] = useDeleteBillMutation();

  // Kapsam dizisi her çizimde yeni olabilir — içerik değişince istek atılsın
  const scopeKey = firmIds.join(",");
  const scopeRef = useRef(firmIds);
  scopeRef.current = firmIds;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, scopeKey]);

  useEffect(() => {
    if (!id || !auth?.accessToken) return;
    getBills({
      id,
      token: auth.accessToken,
      firmIds: scopeRef.current,
      page: debouncedPage,
      pageSize: PAGE_SIZE,
      search: debouncedSearch || "",
    });
  }, [id, auth?.accessToken, getBills, scopeKey, debouncedPage, debouncedSearch, refreshKey]);

  const serverFirms = billsResponse?.data?.firms;
  useEffect(() => {
    if (Array.isArray(serverFirms) && serverFirms.length > 0) onFirmsLoaded?.(serverFirms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverFirms]);

  useEffect(() => {
    if (deleteResult.isSuccess) {
      onNotify?.({ severity: "success", message: "Fatura başarıyla silindi." });
      deleteResult.reset();
      onBillDeleted?.();
      getBills({
        id,
        token: auth.accessToken,
        firmIds: scopeRef.current,
        page: debouncedPage,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || "",
      });
    }
    if (deleteResult.isError) {
      onNotify?.({
        severity: "error",
        message: deleteResult.error?.data?.message || "Fatura silinirken bir hata oluştu.",
      });
      deleteResult.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteResult.isSuccess, deleteResult.isError]);

  const groups = useMemo(() => groupBills(billsResponse?.data?.bills || []), [billsResponse]);
  const total = billsResponse?.data?.total ?? 0;
  const loading = isLoading || isFetching || deleteResult.isLoading;
  const canDelete = !readOnly && hasPermission("bills.delete");

  const askDelete = (bill) => {
    setSkipQuotaEffect(false);
    setConfirm({ open: true, bill });
  };

  const confirmDelete = () => {
    if (!confirm.bill) return;
    deleteBill({
      id: confirm.bill._id,
      ...(skipQuotaEffect && hasPermission("bills.deleteWithoutQuota") ? { skipQuotaEffect: true } : {}),
      token: auth.accessToken,
    });
    setConfirm({ open: false, bill: null });
  };

  return (
    <section className="flex flex-col h-full min-h-0 bg-gray-50/60" aria-label="Faturalar">
      <div className="shrink-0 px-3 pt-3 pb-2 space-y-2 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center shrink-0">
            <FaFileInvoice className="w-3.5 h-3.5 text-amber-500" />
          </div>
          <h3 className="text-sm font-bold text-gray-800">Faturalar</h3>
          <span className="ml-auto text-xs text-gray-400 tabular-nums">{loading ? "…" : total}</span>
        </div>
        <label className="relative block">
          <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Fatura no veya ürün"
            aria-label="Fatura no veya ürün ara"
            className="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </label>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-2 py-2 space-y-2">
        {loading && groups.length === 0 ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-3 animate-pulse">
              <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
              <div className="h-3.5 w-36 bg-gray-200 rounded mb-1.5" />
              <div className="h-3 w-28 bg-gray-100 rounded" />
            </div>
          ))
        ) : isError ? (
          <p className="text-xs text-red-600 text-center py-8">Faturalar alınamadı.</p>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <FaFileInvoice className="w-7 h-7 text-gray-300 mb-2" />
            <p className="text-sm font-semibold text-gray-600">Fatura yok</p>
            {debouncedSearch && <p className="text-xs text-gray-400">Aramaya uygun işlem bulunamadı.</p>}
          </div>
        ) : (
          groups.map((group) => {
            const bill = group.lines[0];
            const single = group.lines.length === 1;
            const details = getBillTypeDetails(group.billType, group.quotaShare);
            const note = single ? extractBillNote(bill) : "";
            return (
              <div
                key={group.key}
                role="button"
                tabIndex={0}
                onClick={() => setDetail({ open: true, bill: group })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setDetail({ open: true, bill: group });
                }}
                className={`group bg-white rounded-xl border border-gray-100 px-3 py-2.5 cursor-pointer hover:border-indigo-200 hover:shadow-sm transition-all ${
                  loading ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {/* Tür renksiz küçük yazı (#326) */}
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    {details.short}
                  </span>
                  <span className="ml-auto text-[11px] text-gray-400 whitespace-nowrap">
                    {FormatDateHourString(group.createdInfo)}
                  </span>
                  {single && canDelete && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        askDelete(bill);
                      }}
                      className="p-1 -mr-1 text-red-300 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Faturayı Sil"
                      aria-label="Faturayı Sil"
                    >
                      <FaTrashAlt className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {showFirmName && group.firmName && (
                  <p
                    className={`text-[11px] truncate mb-0.5 ${group.isSubFirmBill ? "text-violet-700 font-SemiBold" : "text-gray-500"}`}
                    title={group.firmName}
                  >
                    {group.firmName}
                  </p>
                )}
                {group.productSummary.map((p) => (
                  <p key={p.name} className="text-sm font-semibold text-gray-800 truncate">
                    {p.name}{" "}
                    <span className="text-gray-400">
                      × <span className="font-Bold text-gray-700 tabular-nums">{p.quota.toLocaleString("tr-TR")}</span>
                    </span>
                  </p>
                ))}
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-[11px] text-gray-400 truncate min-w-0">
                    {group.billNo}
                    {!single && ` · ${group.lines.length} kalem`}
                    {group.billerName ? ` · ${group.billerName}` : ""}
                  </p>
                  {group.billType === "Bill" && group.totalIncome ? (
                    <span className="ml-auto text-sm font-Bold text-emerald-600 tabular-nums whitespace-nowrap">
                      {money(group.totalIncome)}
                    </span>
                  ) : null}
                </div>
                {note && <p className="text-[11px] text-gray-500 italic truncate mt-0.5">{note}</p>}
              </div>
            );
          })
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="shrink-0 border-t border-gray-200 bg-white px-2 py-1.5">
          <CustomPagination currentPage={page} totalCount={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </div>
      )}

      <BillDetailDrawer
        open={detail.open}
        onClose={() => setDetail({ open: false, bill: null })}
        bill={detail.bill}
        typeDetails={detail.bill ? getBillTypeDetails(detail.bill.billType, detail.bill.quotaShare) : null}
        note={detail.bill ? extractBillNote(detail.bill) : ""}
      />

      <ConfirmationModal
        open={confirm.open}
        onClose={() => setConfirm({ open: false, bill: null })}
        onConfirm={confirmDelete}
        title="Faturayı Sil"
        message={`"${confirm.bill?.billNo}" numaralı faturayı silmek istediğinize emin misiniz?`}
        confirmText="Sil"
        isLoading={deleteResult.isLoading}
      >
        {hasPermission("bills.deleteWithoutQuota") && (
          <label className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={skipQuotaEffect}
              onChange={(e) => setSkipQuotaEffect(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-amber-600 shrink-0"
            />
            <span className="text-sm text-amber-900 leading-snug">
              <span className="font-semibold">Firma kotası etkilenmesin.</span> Fatura silinir ama firmanın
              ürün kotası geri alınmaz/değiştirilmez.
            </span>
          </label>
        )}
      </ConfirmationModal>
    </section>
  );
}
